import {
  Box,
  Checkbox,
  Icon,
  TextField,
  Typography
} from "@outschool/backpack";
import { faArrowRight } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { SelfElectedStateFundingProgram } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { FUNDING_OPTION_KEYS } from "../shared/publicFundingConstants";

export function useStateFundingFormOptions(stateAbbreviation: string) {
  const { t } = useTranslation(
    "client\\components\\banners\\SelfIdentificationPublicFunding"
  );
  switch (stateAbbreviation.toUpperCase()) {
    case "AL":
      return {
        fundingOptions: [
          {
            topLabel: t("CHOOSE Program"),
            subLabel: t(
              "The CHOOSE Act creates a flexible, family-directed education savings program with up to $7,000 per student (or $2,000 for homeschoolers), funded through state tax credits."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "CHOOSE",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "AZ":
      return {
        fundingOptions: [
          {
            topLabel: t("Empowerment Scholarship Account (ESA)"),
            subLabel: t(
              "State funds that families can use for private school, online learning, tutoring, and other educational expenses—including Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "ESA",
            inactiveValue: ""
          },
          {
            topLabel: t("Purchase through a state issued debit card"),
            subLabel: t(
              "Arizona Only: I use my program issued debit card to purchase Outschool classes."
            ),
            key: FUNDING_OPTION_KEYS.stateDebitCard,
            activeValue: true,
            inactiveValue: false
          },
          {
            topLabel: t("Reimbursement through my ESA"),
            subLabel: t(
              "I submit receipts for reimbursement from my state program."
            ),
            key: FUNDING_OPTION_KEYS.reimbursement,
            activeValue: true,
            inactiveValue: false
          }
        ],
        isSupportedByVendor: true
      };
    case "AR":
      return {
        fundingOptions: [
          {
            topLabel: t("Education Freedom Accounts (EFA)"),
            subLabel: t(
              "State-funded program that helps eligible families pay for private school, tutoring, and online classes like Outschool."
            ),
            key: "stateProgramName",
            activeValue: "EFA",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "FL":
      return {
        fundingOptions: [
          {
            topLabel: t("Florida Empowerment Scholarships (UA, PEP, EO)"),
            subLabel: t(
              "State scholarships that help eligible families pay for private school, online learning, and other education expenses—including Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "EFA",
            inactiveValue: ""
          },
          {
            topLabel: t("Reimbursement through my FES UA/EO scholarship"),
            subLabel: t(
              "I submit receipts for reimbursement from my state program."
            ),
            key: FUNDING_OPTION_KEYS.reimbursement,
            activeValue: true,
            inactiveValue: false
          }
        ],
        isSupportedByVendor: true
      };
    case "NH":
      return {
        fundingOptions: [
          {
            topLabel: t("Education Freedom Accounts (EFA)"),
            subLabel: t(
              "State funds that help eligible families pay for private school, tutoring, and approved educational services like Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "EFA",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "NY":
      return {
        fundingOptions: [
          {
            topLabel: t("Children’s Scholarship Fund of NY (CSF)"),
            subLabel: t(
              "Privately funded scholarships that help low-income families in NYC afford private school and educational resources."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "CSF",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "NC":
      return {
        fundingOptions: [
          {
            topLabel: t("Education Student Accounts (ESA+) program"),
            subLabel: t(
              "A state program that provides funds for eligible students to use on private school, tutoring, and online classes like Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "ESA+",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "SC":
      return {
        fundingOptions: [
          {
            topLabel: t("Education Scholarship Trust Fund (ESTF)"),
            subLabel: t(
              "State-funded program that helps eligible families pay for private school, tutoring, and online learning like Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "ESTF",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "TX":
      return {
        fundingOptions: [
          {
            topLabel: t("Parent-Directed Special Education Services (PDSES)"),
            subLabel: t(
              "Funds for eligible families to purchase educational services and resources for students with special needs—including online learning."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "PDSES",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "UT":
      return {
        fundingOptions: [
          {
            topLabel: t("Utah Fits All"),
            subLabel: t(
              "State-funded scholarship that gives families flexible funds to pay for private school, tutoring, and online education like Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "Utah Fits All",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "GA":
      return {
        fundingOptions: [
          {
            topLabel: t("Promise Scholarship"),
            subLabel: t(
              "Proposed state program to give families education funds for private school, tutoring, and learning resources like Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "Promise Scholarship",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "WY":
      return {
        fundingOptions: [
          {
            topLabel: t("Education Savings Account"),
            subLabel: t(
              "A state-funded program that helps eligible families pay for private school, tutoring, and online classes like Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "Education Savings Account",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "LA":
      return {
        fundingOptions: [
          {
            topLabel: t("Giving All True Opportunity to Rise (GATOR)"),
            subLabel: t(
              "State-funded scholarships that help eligible students access private school, tutoring, and online educational options like Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "GATOR",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    case "WV":
      return {
        fundingOptions: [
          {
            topLabel: t("Hope Scholarship"),
            subLabel: t(
              "State education funds that eligible families can use for private school, tutoring, and approved online learning like Outschool."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "Hope Scholarship",
            inactiveValue: ""
          }
        ],
        isSupportedByVendor: true
      };
    default:
      return {
        fundingOptions: [
          {
            topLabel: t("Use my state portal to make purchases"),
            subLabel: t(
              "I make purchases through my state market place (Classwallet, Odyssey, Students First, Step Up For Students, etc.)."
            ),
            key: FUNDING_OPTION_KEYS.stateProgramName,
            activeValue: "default",
            inactiveValue: ""
          },
          {
            topLabel: t("Reimbursement through my state program"),
            subLabel: t(
              "I submit receipts for reimbursement from my state program."
            ),
            key: FUNDING_OPTION_KEYS.reimbursement,
            activeValue: true,
            inactiveValue: false
          }
        ],
        isSupportedByVendor: false
      };
  }
}

interface FundingOptionItemProps {
  option: {
    key: string;
    topLabel: string;
    subLabel: string;
    activeValue: any;
    inactiveValue: any;
  };
  isChecked: boolean;
  onToggle: (key: string, value: any) => void;
}

export const FundingOptionItem: React.FC<FundingOptionItemProps> = ({
  option,
  isChecked,
  onToggle
}) => (
  <Box flex sx={{ flexDirection: "row", gap: 10, mt: 10 }}>
    <Checkbox
      sx={{
        width: "fit-content",
        alignSelf: "flex-start",
        py: 0
      }}
      id={option.key}
      checked={isChecked}
      onChange={({ target }) => {
        if (target.checked) {
          onToggle(option.key, option.activeValue);
        } else {
          onToggle(option.key, option.inactiveValue);
        }
      }}
    />
    <label
      htmlFor={option.key}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        cursor: "pointer"
      }}
    >
      <Typography variant="body1">{option.topLabel}</Typography>
      <Typography variant="body2">{option.subLabel}</Typography>
    </label>
  </Box>
);

interface CharterSchoolFundingOptionProps {
  isChecked: boolean;
  onToggle: (key: string, value: boolean) => void;
  t: (key: string) => string;
}

export const CharterSchoolFundingOption: React.FC<
  CharterSchoolFundingOptionProps
> = ({ isChecked, onToggle, t }) => (
  <Box flex sx={{ flexDirection: "row", gap: 10 }}>
    <Checkbox
      sx={{
        width: "fit-content",
        alignSelf: "flex-start",
        py: 0
      }}
      id="charter"
      checked={isChecked}
      onChange={({ target }) => {
        onToggle(FUNDING_OPTION_KEYS.charter, target.checked);
      }}
    />
    <label
      htmlFor="charter"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        cursor: "pointer"
      }}
    >
      <Typography variant="body1">{t("Charter School Funding")}</Typography>
      <Typography variant="body2">
        {t("My charter school purchases Outschool enrollments for my learner.")}
      </Typography>
    </label>
  </Box>
);

interface DifferentStateProgramOptionProps {
  isChecked: boolean;
  programName: string;
  onToggle: (key: string, value: string) => void;
  onProgramNameChange: (value: string) => void;
  t: (key: string) => string;
}

export const DifferentStateProgramOption: React.FC<
  DifferentStateProgramOptionProps
> = ({ isChecked, programName, onToggle, onProgramNameChange, t }) => (
  <Box flex sx={{ flexDirection: "row", gap: 10, mt: 10 }}>
    <Checkbox
      sx={{
        width: "fit-content",
        alignSelf: "flex-start",
        py: 0
      }}
      id="otherProgramName"
      checked={isChecked}
      onChange={({ target }) => {
        if (!target.checked) {
          // eslint-disable-next-line i18next/no-literal-string
          onToggle("otherProgramName", "");
        } else {
          const input = document.getElementById("otherProgramNameInput");
          if (input) {
            input.focus();
          }
        }
      }}
    />
    <label
      htmlFor="otherProgramName"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        cursor: "pointer",
        width: "100%"
      }}
    >
      <Typography variant="body1">
        {t("I use a different state program")}
      </Typography>
      <TextField
        id="otherProgramNameInput"
        value={programName}
        size="small"
        placeholder={t("What program do you participate in?")}
        onChange={({ target }) => {
          onProgramNameChange(target.value);
        }}
        sx={{ width: "100%", backgroundColor: "transparent" }}
      />
    </label>
  </Box>
);

function StateFundingWrapper({
  handleContinue,
  handleSelfPayExit,
  selfElectedStateFundingProgram,
  children,
  setFundingOptions,
  hideButtons = false,
  isSupportedByVendor
}: {
  handleContinue: () => void;
  handleSelfPayExit: () => void;
  selfElectedStateFundingProgram: SelfElectedStateFundingProgram;
  children: React.ReactNode;
  setFundingOptions: (key: string, value: boolean | string) => void;
  hideButtons?: boolean;
  isSupportedByVendor: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\banners\\SelfIdentificationPublicFunding"
  );
  const Link = useLinkComponent();

  const continueDisabled = !Object.values(
    selfElectedStateFundingProgram.fundingOptions
  ).some(option => !!option);

  return (
    <Box>
      <Typography variant="h4" emphasized>
        {t("Tell us how you’ll fund your learner’s classes")}
      </Typography>
      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: 5,
          mt: 10,
          p: 20,
          backgroundColor: "primary.50",
          borderRadius: 10
        }}
      >
        <Typography variant="body1">
          {t(
            "Each state has different options for funding your child’s education."
          )}
        </Typography>
        <Typography variant="body1" emphasized>
          {t("State Funded Programs (ESA, micro-grants, scholarship)")}
        </Typography>
        <Typography variant="body1">
          {t(
            "Selecting a state program helps us personalize your experience, but it doesn’t guarantee coverage."
          )}
        </Typography>
        <Link
          trackingName="public_funding_self_identification_funding_options_learn_more"
          sx={{
            display: "flex",
            alignItems: "center",
            width: "fit-content"
          }}
          to="/esa"
          target="_blank"
        >
          {t("Learn more")}
          <Icon icon={faArrowRight} sx={{ marginLeft: 5 }} />
        </Link>
        {children}
        {isSupportedByVendor && (
          <DifferentStateProgramOption
            isChecked={
              !!selfElectedStateFundingProgram.fundingOptions.otherProgramName
            }
            programName={
              selfElectedStateFundingProgram.fundingOptions.otherProgramName ||
              ""
            }
            onToggle={setFundingOptions}
            onProgramNameChange={value => {
              // eslint-disable-next-line i18next/no-literal-string
              setFundingOptions("otherProgramName", value);
            }}
            t={t}
          />
        )}
        <Typography variant="body1" emphasized sx={{ mt: 10 }}>
          {t("Other funding options")}
        </Typography>
        <CharterSchoolFundingOption
          isChecked={selfElectedStateFundingProgram.fundingOptions.charter}
          onToggle={setFundingOptions}
          t={t}
        />
      </Box>
      {!hideButtons && (
        <>
          <TrackedButton
            variant="contained"
            trackingName="public_funding_self_identification_funding_options_continue"
            trackingSharedProperties={selfElectedStateFundingProgram}
            onClick={handleContinue}
            disabled={continueDisabled}
            sx={{
              width: "100%",
              mt: 20
            }}
          >
            {t("Continue")}
          </TrackedButton>
          <TrackedButton
            variant="outlined"
            trackingName="public_funding_self_identification_funding_options_self_pay"
            trackingSharedProperties={selfElectedStateFundingProgram}
            onClick={handleSelfPayExit}
            sx={{
              width: "100%",
              mt: 20
            }}
          >
            {t("I pay for classes myself")}
          </TrackedButton>
        </>
      )}
    </Box>
  );
}

export function DefaultStateFundingForm({
  handleContinue,
  handleSelfPayExit,
  setFundingOptions,
  selfElectedStateFundingProgram
}: {
  handleContinue: () => void;
  handleSelfPayExit: () => void;
  setFundingOptions: (key: string, value: boolean | string) => void;
  selfElectedStateFundingProgram: SelfElectedStateFundingProgram;
}) {
  const { fundingOptions, isSupportedByVendor } = useStateFundingFormOptions(
    selfElectedStateFundingProgram.stateAbbreviation
  );

  return (
    <StateFundingWrapper
      handleContinue={handleContinue}
      handleSelfPayExit={handleSelfPayExit}
      selfElectedStateFundingProgram={selfElectedStateFundingProgram}
      setFundingOptions={setFundingOptions}
      isSupportedByVendor={isSupportedByVendor}
    >
      {fundingOptions.map(option => (
        <FundingOptionItem
          key={option.key}
          option={option}
          isChecked={
            !!selfElectedStateFundingProgram.fundingOptions[
              option.key as keyof typeof selfElectedStateFundingProgram.fundingOptions
            ]
          }
          onToggle={setFundingOptions}
        />
      ))}
    </StateFundingWrapper>
  );
}
